import { Divider } from '@mui/joy'
import dayjs from 'dayjs'
import { last, pick, round } from 'lodash'
import { Flag } from 'lucide-react'
import React from 'react'
import { useSearchParams } from 'react-router'
import AnalysisCard, { AnalysisType } from '../components/AnalysisCard'
import CategoryScore, { CategoryScoreType } from '../components/CategoryScore'
import ConversationAnalysis from '../components/ConversationAnalysis'
import DescriptiveFeedback from '../components/DescriptiveFeedback'
import EnglishLevelBar from '../components/EnglishLevelBar'
import EnglishLevelDisplay from '../components/EnglishLevelDisplay'
import FluencyCard from '../components/FluencyCard'
import FluencyResult from '../components/FluencyResult'
import ResultCard from '../components/ResultCard'
import ResultNavbar from '../components/ResultNavbar'
import SpeakingMetrics from '../components/SpeakingMetrics'
import UserMessageTranscript from '../components/UserMessageTranscript'
import WordUsageChart from '../components/WordUsageChart'
import BotMessage from '@/components/chat/BotMessage'
import UserMessage from '@/components/chat/UserMessage'
import { ButtonGroup, Column, Flex, Row, Show, Text } from '@/components/core'
import HubspotForm from '@/components/HubspotForm.tsx'
import { Match, Switch } from '@/components/switch'
import { useAuth } from '@/contexts/AuthContext.tsx'
import VideoBox from '@/features/Result/components/VideoBox.tsx'
import { classNameBuilder } from '@/helpers'
import {
  ChatItem,
  FluencyTestResult,
  getFluencyTestResult
} from '@/services/getFluencyTestResult.ts'

export default function Result() {
  const [searchParams] = useSearchParams()

  const { configurations } = useAuth()

  const [conversationView, setConversationView] = React.useState('error-only')
  const [fluencyTestData, setFluencyTestData] =
    React.useState<FluencyTestResult>()

  const code = searchParams.get('code')

  React.useEffect(() => {
    if (code) {
      getFluencyTestResult(code).then((testData) => {
        setFluencyTestData(testData)
      })
    }
  }, [code])

  const isDemonstrationTest = (
    import.meta.env.VITE_API_BASE_URL || ''
  ).includes('staging')

  const conversation = React.useMemo(() => {
    if (!fluencyTestData) {
      return [] as ChatItem[]
    }
    if (conversationView === 'error-only') {
      return fluencyTestData.chat.reduce((acc, chat, index) => {
        if (!chat.grammarErrors || !chat.grammarErrors.length) {
          const lastItem = last(acc)
          if (lastItem && lastItem.role === 'hidden-chat') {
            lastItem.amount += 1
            return acc
          }
          return [
            ...acc,
            {
              role: 'hidden-chat',
              amount: 1,
              _id: `hidden-chat-${index}`
            } as ChatItem
          ]
        }

        return [...acc, chat]
      }, [] as ChatItem[])
    }

    return fluencyTestData.chat
  }, [conversationView, fluencyTestData])

  if (!fluencyTestData) return null

  return (
    <Column className="absolute min-h-screen w-full space-y-4 overflow-x-hidden py-20">
      <ResultNavbar />
      {isDemonstrationTest && (
        <Column className="w-full items-center self-center rounded-xl bg-[#dada66] p-[16px] px-[24px] shadow lg:w-[930px] lg:bg-gradient-128deg lg:from-white lg:to-[#dada66]">
          <Text.Translated
            size="sm"
            translationKey="demonstration_result_warning"
          />
        </Column>
      )}
      <ResultCard id="result">
        <Row className="w-full justify-between gap-2 max-lg:flex-wrap max-lg:justify-center">
          <Column className="gap-[20px] max-lg:items-center max-lg:pb-12">
            <Column className="gap-[4px]">
              <Text.Translated
                className="!text-electric-indigo-800/[.50]"
                size="xs"
                translationKey="performed_by_app_resultado_page"
                values={[fluencyTestData.student.name]}
              />
              <Text.Translated
                className="!text-electric-indigo-800/[.50]"
                size="xs"
                translationKey="performed_on_app_resultado_page"
                values={[
                  dayjs(fluencyTestData.finishedAt).format('DD/MM/YYYY')
                ]}
              />
            </Column>
            <Column className="gap-[8px] max-lg:items-center">
              <Row className="center relative gap-[12px] align-middle max-lg:flex-col max-lg:items-center">
                <Column className="absolute left-[-85px] h-[40px] w-[40px] items-center justify-center rounded-full bg-[#09D4E1] max-lg:relative max-lg:left-auto">
                  <Flag
                    size={16}
                    className="text-electric-indigo-800"
                    strokeWidth={2}
                  />
                </Column>
                <Text.Translated
                  size="3xl"
                  className="!text-electric-indigo-700 max-lg:text-lg"
                  fontWeight="bold"
                  translationKey="overall_result_app_resultado_page"
                />
              </Row>
              <EnglishLevelBar
                level={fluencyTestData.fluencyDataOverview?.cefrScore?.overall}
              />
            </Column>
          </Column>
          <EnglishLevelDisplay
            cefrLevel={fluencyTestData.fluencyDataOverview?.cefrScore?.overall}
          />
        </Row>
        <Flex className="flex-col justify-between py-12 max-lg:space-y-8 max-lg:px-8 lg:flex-row lg:space-x-24">
          <Row className="w-full">
            <DescriptiveFeedback
              feedbacks={fluencyTestData.chatGptOverview?.reasons || []}
            />
          </Row>
          <Row className="lg:w-[70%]">
            <CategoryScore
              categoryScore={
                pick(fluencyTestData.fluencyDataOverview.score, [
                  'pronunciation',
                  'fluency',
                  'grammar',
                  'coherence',
                  'vocabulary'
                ]) as CategoryScoreType
              }
            />
          </Row>
        </Flex>
        <Flex className="flex-col px-8 lg:px-0">
          <Text.Translated
            color="600"
            fontWeight="bold"
            size="lg"
            translationKey="conversation_analysis_app_resultado_page"
          />
          <ConversationAnalysis
            items={[
              {
                id: 'grammarErrors',
                label:
                  'conversation_analysis_grammar_errors_app_resultado_page',
                value: fluencyTestData.chat?.reduce(
                  (acc, chat) => acc + (chat.grammarErrors?.length || 0),
                  0
                )
              },
              ...Object.entries(
                pick(
                  fluencyTestData.vocabularyDataOverview,
                  'wordCount',
                  'uniqueWordCount'
                )
              ).map(([key, value]) => {
                return {
                  id: key,
                  label:
                    {
                      wordCount:
                        'conversation_analysis_words_app_resultado_page',
                      uniqueWordCount:
                        'conversation_analysis_unique_words_app_resultado_page'
                    }[key] || '',
                  value
                }
              })
            ]}
          />
        </Flex>
      </ResultCard>
      <ResultCard
        id="fluency-analysis"
        indicatorPosition="left"
        indicatorColor="orange"
        title="fluency_analysis_app_resultado_page"
      >
        <FluencyResult
          fluencyScore={
            (
              fluencyTestData.fluencyDataOverview.score ||
              fluencyTestData.fluencyDataOverview.speechaceScore
            ).fluency
          }
        />
        <FluencyCard
          data={{
            speechRate:
              fluencyTestData.fluencyDataOverview?.fluencyMetrics?.speechRate,
            articulationRate:
              fluencyTestData.fluencyDataOverview?.fluencyMetrics
                ?.articulationRate
          }}
        />
        <div className="max-lg:px-8 lg:hidden">
          <Divider />
        </div>
        <Flex className="flex-row flex-wrap gap-2 max-lg:flex-col max-lg:gap-[0px]">
          <SpeakingMetrics
            title="speaking_time_speaking_metrics"
            label="seconds_label_speaking_metrics"
            goodValueType="more"
            goodValue={round(
              fluencyTestData.fluencyDataOverview?.fluencyMetrics?.duration *
                0.8,
              0
            )}
            currentValue={round(
              fluencyTestData.fluencyDataOverview?.fluencyMetrics?.duration -
                fluencyTestData.fluencyDataOverview?.fluencyMetrics
                  ?.allPauseDuration,
              1
            )}
            markers={[
              {
                limit:
                  fluencyTestData.fluencyDataOverview?.fluencyMetrics
                    ?.duration * 0.7,
                variant: 'bad'
              },
              {
                limit:
                  fluencyTestData.fluencyDataOverview?.fluencyMetrics
                    ?.duration * 0.8,
                variant: 'warning'
              },
              {
                limit:
                  fluencyTestData.fluencyDataOverview?.fluencyMetrics?.duration,
                variant: 'good'
              }
            ]}
          />
          <div className="max-lg:px-8 lg:hidden">
            <Divider />
          </div>
          <SpeakingMetrics
            title="pause_count_speaking_metrics"
            label="pauses_label_speaking_metrics"
            goodValueType="until"
            goodValue={round(
              fluencyTestData.fluencyDataOverview?.fluencyMetrics?.duration -
                10,
              0
            )}
            currentValue={
              fluencyTestData.fluencyDataOverview?.fluencyMetrics?.allPauseCount
            }
            markers={[
              {
                limit:
                  fluencyTestData.fluencyDataOverview?.fluencyMetrics
                    ?.duration - 10,
                variant: 'good'
              },
              {
                limit:
                  fluencyTestData.fluencyDataOverview?.fluencyMetrics
                    ?.duration + 15,
                variant: 'warning'
              },
              {
                limit:
                  fluencyTestData.fluencyDataOverview?.fluencyMetrics
                    ?.duration + 30,
                variant: 'bad'
              }
            ]}
          />
          <div className="max-lg:px-8 lg:hidden">
            <Divider />
          </div>
          <SpeakingMetrics
            title="total_pause_duration_speaking_metrics"
            label="seconds_label_speaking_metrics"
            goodValueType="until"
            goodValue={round(
              fluencyTestData.fluencyDataOverview?.fluencyMetrics?.duration *
                0.25,
              0
            )}
            currentValue={round(
              fluencyTestData.fluencyDataOverview?.fluencyMetrics
                ?.allPauseDuration,
              1
            )}
            markers={[
              {
                limit:
                  fluencyTestData.fluencyDataOverview?.fluencyMetrics
                    ?.duration * 0.25,
                variant: 'good'
              },
              {
                limit:
                  fluencyTestData.fluencyDataOverview?.fluencyMetrics
                    ?.duration * 0.5,
                variant: 'warning'
              },
              {
                limit:
                  fluencyTestData.fluencyDataOverview?.fluencyMetrics?.duration,
                variant: 'bad'
              }
            ]}
          />
          <div className="max-lg:px-8 lg:hidden">
            <Divider />
          </div>
          <SpeakingMetrics
            title="longest_phrase_without_hesitation_speaking_metrics"
            label="seconds_label_speaking_metrics"
            goodValue={4}
            goodValueType="more"
            currentValue={round(
              fluencyTestData.fluencyDataOverview?.fluencyMetrics?.maxLengthRun,
              1
            )}
            markers={[
              {
                limit: 2,
                variant: 'bad'
              },
              {
                limit: 4,
                variant: 'warning'
              },
              {
                limit: 6,
                variant: 'good'
              }
            ]}
          />
        </Flex>
      </ResultCard>
      {!!configurations?.showHubspotForm && (
        <ResultCard
          id="grammar-analysis"
          variant="highlight"
          title="site_demonstration_contact_app_resultado_page"
        >
          <HubspotForm formId={configurations.showHubspotForm} />
        </ResultCard>
      )}
      <ResultCard
        id="grammar-analysis"
        indicatorPosition="left"
        indicatorColor="blue"
        title="grammar_vocabulary_analysis_app_resultado_page"
      >
        <Column className="space-y-2 max-lg:px-8">
          <AnalysisCard
            score={
              (
                fluencyTestData.fluencyDataOverview.score ||
                fluencyTestData.fluencyDataOverview.speechaceScore
              ).grammar
            }
            analysisType={AnalysisType.GRAMMAR}
            chartItems={fluencyTestData.fluencyDataOverview?.grammarMetrics}
          />
          <div className="lg:hidden">
            <Divider />
          </div>
          <AnalysisCard
            score={
              (
                fluencyTestData.fluencyDataOverview.score ||
                fluencyTestData.fluencyDataOverview.speechaceScore
              ).vocabulary
            }
            analysisType={AnalysisType.VOCABULARY}
            chartItems={fluencyTestData.fluencyDataOverview?.vocabularyMetrics}
          />
          <div className="lg:hidden">
            <Divider />
          </div>
          <AnalysisCard
            score={
              (
                fluencyTestData.fluencyDataOverview.score ||
                fluencyTestData.fluencyDataOverview.speechaceScore
              ).coherence
            }
            analysisType={AnalysisType.COHERENCE}
            chartItems={fluencyTestData.fluencyDataOverview?.coherenceMetrics}
          />
        </Column>
      </ResultCard>
      {fluencyTestData.vocabularyDataOverview && (
        <ResultCard
          id="grammar-analysis"
          indicatorPosition="left"
          indicatorColor="blue"
          title="advanced_words_usage_app_resultado_page"
        >
          <WordUsageChart
            data={fluencyTestData.vocabularyDataOverview.wordsByCefrLevel}
          />
        </ResultCard>
      )}
      <ResultCard
        id="conversation-recording"
        indicatorPosition="left"
        indicatorColor="green"
        title="conversation_analysis_app_resultado_page"
      >
        <Flex className="px-8 lg:px-0">
          <ConversationAnalysis
            items={[
              {
                id: 'grammarErrors',
                label: 'grammar_errors_conversation_analysis',
                value: fluencyTestData.chat?.reduce(
                  (acc, chat) => acc + (chat.grammarErrors?.length || 0),
                  0
                )
              },
              ...Object.entries(
                pick(
                  fluencyTestData.vocabularyDataOverview,
                  'sentenceCount',
                  'wordCount',
                  'uniqueWordCount',
                  'averageWordCountBySentence',
                  'averageUniqueWordCountBySentence',
                  'syllableCount',
                  'wordCountWithTwoOrMoreSyllables'
                )
              ).map(([key, value]) => {
                return {
                  id: key,
                  label:
                    {
                      sentenceCount: 'sentences_conversation_analysis',
                      wordCount: 'words_conversation_analysis',
                      averageWordCountBySentence:
                        'average_words_per_sentence_conversation_analysis',
                      uniqueWordCount: 'unique_words_conversation_analysis',
                      averageUniqueWordCountBySentence:
                        'average_unique_words_per_sentence_conversation_analysis',
                      syllableCount: 'total_syllables_conversation_analysis',
                      wordCountWithTwoOrMoreSyllables:
                        'words_with_two_or_more_syllables_conversation_analysis'
                    }[key] || '',
                  value
                }
              })
            ]}
          />
        </Flex>
        <div className="!my-10 max-lg:px-8">
          <Divider />
        </div>
        <Row className="!mb-8 !mt-0 w-full items-center justify-between gap-2 max-lg:flex-col max-lg:justify-center">
          <Text.Translated
            color="600"
            fontWeight="bold"
            size="lg"
            translationKey="conversation_recording_app_resultado_page"
          />
          <Row className="items-center justify-center">
            <div className="max-lg:hidden">
              <ButtonGroup
                size="md"
                value={conversationView}
                options={[
                  {
                    label: 'view_errors_only_app_resultado_page',
                    value: 'error-only'
                  },
                  {
                    label: 'view_full_conversation_app_resultado_page',
                    value: 'all'
                  }
                ]}
                onClick={setConversationView}
              />
            </div>
            <div className="lg:hidden">
              <ButtonGroup
                size="sm"
                value={conversationView}
                options={[
                  {
                    label: 'view_errors_only_app_resultado_page',
                    value: 'error-only'
                  },
                  {
                    label: 'view_full_conversation_app_resultado_page',
                    value: 'all'
                  }
                ]}
                onClick={setConversationView}
              />
            </div>
          </Row>
        </Row>
        <Column id="conversation-section" className="space-y-6 max-lg:px-8">
          {conversation.map((message) => {
            return (
              <Column
                key={message._id}
                className={classNameBuilder(
                  message.role === 'hidden-chat' ? '' : 'w-2/3 lg:w-[45%]',
                  message.role === 'user' ? 'self-end' : ''
                )}
              >
                <Switch>
                  <Match when={message.role === 'hidden-chat'}>
                    <Row className="relative my-4 h-px w-full items-center justify-center bg-gray-50">
                      <Column className="absolute h-6 w-56 items-center justify-center rounded-full border border-gray-50 bg-white">
                        <Text.Translated
                          color="200"
                          size="12"
                          fontWeight="semibold"
                          transform="uppercase"
                          translationKey={
                            message.amount > 1
                              ? 'hidden_audio_single_app_resultado_page'
                              : 'hidden_audio_plural_app_resultado_page'
                          }
                        />
                      </Column>
                    </Row>
                  </Match>
                  <Match when={message.role === 'assistant'}>
                    <BotMessage
                      className="border-[1px] border-[#CDAEFE]"
                      showControls
                      audioUrl={
                        message.audioUrl
                          ? `${import.meta.env.VITE_RECORD_BASE_URL}/${
                              message.audioUrl
                            }`
                          : undefined
                      }
                    />
                    <Column className="mt-2 justify-center border-l-2 border-[#B486FE] px-2 py-2">
                      <Text size="sm" className="!text-electric-indigo-900">
                        {message.content}
                      </Text>
                    </Column>
                  </Match>
                  <Match when={message.role === 'user'}>
                    <Column className="w-full items-end self-end lg:w-[358px]">
                      {!!message.recordUrl && (
                        <UserMessage
                          audioUrl={message.recordUrl}
                          statusFeedback={false}
                          showControls
                          message={
                            message.timeline == 'topic-selection'
                              ? message.content
                              : undefined
                          }
                        />
                      )}
                    </Column>
                    <Show
                      when={message.timeline !== 'topic-selection'}
                      fallback={
                        <Column className="mt-2 items-end justify-center border-r-2 border-[#B486FE] px-2 py-2">
                          <Text size="sm" className="!text-electric-indigo-900">
                            {message.content}
                          </Text>
                        </Column>
                      }
                    >
                      <Show
                        when={!!message.fluencyData}
                        fallback={
                          <Column className="mt-2 items-end justify-center border-r-2 border-[#B486FE] px-2 py-2">
                            <Text
                              size="sm"
                              className="!text-electric-indigo-900"
                            >
                              {message.content}
                            </Text>
                          </Column>
                        }
                      >
                        <UserMessageTranscript
                          className="mt-2"
                          wordsList={message.fluencyData?.word_score_list}
                          grammarErrors={message.grammarErrors}
                          longPauses={message.fluencyData?.fluency?.overall_metrics?.all_pause_list?.filter(
                            (pause) => pause[1] - pause[0] > 100 // Only pauses > 1 second
                          )}
                        />
                      </Show>
                    </Show>
                  </Match>
                </Switch>
              </Column>
            )
          })}
        </Column>
      </ResultCard>
      {!!fluencyTestData.videoRecordUrl && (
        <VideoBox videoUrl={fluencyTestData.videoRecordUrl} />
      )}
    </Column>
  )
}
